import { FormControl, FormGroup, Validators } from '@angular/forms';
import { GenderEnum } from '../core';


const SPECIAL_CHARS_PATTERN: RegExp = /^[^<>%#&?,]+$/;
const SPECIAL_CHARS_TEXT = '<>%#&?,';
const decimalFormat: RegExp = /^\d+([.,]\d+)?$/;
const positiveIntegerFormat: RegExp = /^\d*$/;
const patientId_maxLength: number = 50;
const patientInitials_maxLength: number = 5;
const patientAge_min = 1;
const patientAge_max = 100;
const heightCm_max = 250;
const heightFt_max = 8.2;
const weightKg_max = 250;
const weightLb_max = 551.2;

export const patientIdNotUnique = 'patientIdNotUnique';


/**
* Patient create/edit form model.
*/
export class PatientControl implements IPatientControl {
	patientId = new FormControl<string | null>(null, [Validators.required, Validators.maxLength(patientId_maxLength), Validators.pattern(SPECIAL_CHARS_PATTERN)]);
	patientInitials = new FormControl<string | null>(null, [Validators.required, Validators.maxLength(patientInitials_maxLength), Validators.pattern(SPECIAL_CHARS_PATTERN)]);
	patientGender = new FormControl<GenderEnum | null>(null, [Validators.required]);
	patientAge = new FormControl<number | null>(null, [Validators.pattern(positiveIntegerFormat)]);
	patientInfant = new FormControl<boolean | null>(null);
	patientHeightCm = new FormControl<string | null>(null, [Validators.pattern(decimalFormat), Validators.max(heightCm_max)]);
	patientHeightFt = new FormControl<string | null>(null, [Validators.pattern(decimalFormat), Validators.max(heightFt_max)]);
	patientWeightKg = new FormControl<string | null>(null, [Validators.pattern(decimalFormat), Validators.max(weightKg_max)]);
	patientWeightLb = new FormControl<string | null>(null, [Validators.pattern(decimalFormat), Validators.max(weightLb_max)]);
}

export function setPatientValidators(form: FormGroup<IPatientControl>): void {
	form.controls.patientInfant.valueChanges.subscribe(val => {
		const ctrl = form.controls.patientAge;
		const validators = [Validators.pattern(positiveIntegerFormat)];
		!val && validators.push(Validators.min(patientAge_min), Validators.max(patientAge_max));
		ctrl.setValidators(validators);
		ctrl.updateValueAndValidity({ emitEvent: false });
	});
}

export const patientErrorMsgs: { [key in keyof PatientForm]: { [y: string]: string[] } | null } = {
	patientId: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_PATIENTID'],
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_PATIENTID', patientId_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_PATIENTID', SPECIAL_CHARS_TEXT],
		[`${patientIdNotUnique}`]: ['VALIDATION_MESSAGE_UNIQUE_PATIENTID']
	},
	patientInitials: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_PATIENT_INITIALS'],
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_PATIENT_INITIALS', patientInitials_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_PATIENT_INITIALS', SPECIAL_CHARS_TEXT]
	},
	patientGender: {
		[`${Validators.required.name}`]: ['VALIDALIDATION_MESSAGE_REQUIRED_GENDER']
	},
	patientAge: {
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_NUMERIC_AGE'],
		[`${Validators.min.name}`]: ['VALIDATION_MESSAGE_RANGEVALUE_AGE', patientAge_min.toString(), patientAge_max.toString()],
		[`${Validators.max.name}`]: ['VALIDATION_MESSAGE_RANGEVALUE_AGE', patientAge_min.toString(), patientAge_max.toString()]
	},
	patientInfant: null,
	patientHeightCm: {
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_NUMERIC_HEIGHT_CM'],
		[`${Validators.max.name}`]: ['VALIDATION_MESSAGE_MAX_HEIGHT_CM', heightCm_max.toString()]
	},
	patientHeightFt: {
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_NUMERIC_HEIGHT_FT'],
		[`${Validators.max.name}`]: ['VALIDATION_MESSAGE_MAX_HEIGHT_FT', heightFt_max.toString()]
	},
	patientWeightKg: {
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_NUMERIC_WEIGHT_KG'],
		[`${Validators.max.name}`]: ['VALIDATION_MESSAGE_MAX_WEIGHT_KG', weightKg_max.toString()]
	},
	patientWeightLb: {
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_NUMERIC_WEIGHT_LB'],
		[`${Validators.max.name}`]: ['VALIDATION_MESSAGE_MAX_WEIGHT_LB', weightLb_max.toString()]
	}
}

export interface IPatientControl {
	patientId: FormControl<string | null>;
	patientInitials: FormControl<string | null>;
	patientGender: FormControl<GenderEnum | null>;
	patientAge: FormControl<number | null>;
	patientInfant: FormControl<boolean | null>;
	patientHeightCm: FormControl<string | null>;
	patientHeightFt: FormControl<string | null>;
	patientWeightKg: FormControl<string | null>;
	patientWeightLb: FormControl<string | null>;
}

export interface PatientForm {
	patientId: string;
	patientInitials: string;
	patientGender: GenderEnum;
	patientAge: number | null;
	patientInfant: boolean | null;
	patientHeightCm: string | null;
	patientHeightFt: string | null;
	patientWeightKg: string | null;
	patientWeightLb: string | null;
}
